import { useRef, useState } from "react";
import { toPng } from "html-to-image";
import { toast } from "sonner";
import { ShieldCheck, Share2, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const SHARE_BASE = process.env.REACT_APP_SHARE_BASE || "https://bloqtalk.app";

const BAND_THEME = {
  Baseline: { from: "#0a1420", to: "#0a1628", chip: "bg-bloq-blue/20 text-bloq-blue", accent: "text-bloq-blue" },
  Trusted: { from: "#0b1a17", to: "#0a1c18", chip: "bg-bloq-mint/20 text-bloq-mint", accent: "text-bloq-mint" },
  "High Trust": { from: "#120a1e", to: "#0d0a1e", chip: "bg-bloq-purple/20 text-bloq-purple", accent: "text-bloq-purple" },
};

// A post-worthy, branded trust-badge image. Renders a card + exports it as a PNG so users can
// share the milestone (native share on mobile, download + link-copy on desktop).
export function BandBadgeCard({ band, score }) {
  const { user } = useAuth();
  const cardRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const theme = BAND_THEME[band] || BAND_THEME.Trusted;
  const handle = user?.username ? `@${user.username}` : user?.display_name || "A BLOQ member";
  const profileUrl = `${SHARE_BASE}/u/${encodeURIComponent(user?.username || "")}`;

  const share = async () => {
    if (!cardRef.current) return;
    setBusy(true);
    try {
      const dataUrl = await toPng(cardRef.current, { pixelRatio: 2, cacheBust: true, backgroundColor: "#07070B" });
      const text = `I just reached ${band} on BLOQ Talk 🛡️ — quantum-secure, trust-gated messaging for Web3. My reputation: ${profileUrl}`;
      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], `bloqtalk-${band.toLowerCase().replace(/\s+/g, "-")}-badge.png`, { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], text, title: `BLOQ Talk — ${band}` });
      } else {
        const a = document.createElement("a");
        a.href = dataUrl;
        a.download = file.name;
        a.click();
        try { await navigator.clipboard.writeText(profileUrl); } catch { /* ignore */ }
        toast.success("Badge saved — profile link copied. Post it anywhere!");
      }
    } catch {
      toast.error("Couldn't generate the badge — try again");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="w-full" data-testid="band-badge-card">
      <div
        ref={cardRef}
        className="relative overflow-hidden rounded-3xl border border-white/12 p-6"
        style={{ background: `linear-gradient(135deg, ${theme.from}, #07070B 55%, ${theme.to})` }}
        data-testid="band-badge-visual"
      >
        <div className="flex items-center justify-between">
          <div className="font-heading text-base font-black tracking-tight text-white">
            BLOQ<span className="text-bloq-mint">Talk</span>
          </div>
          <span className={`rounded-full px-2.5 py-1 text-[10px] font-black uppercase tracking-wider ${theme.chip}`}>Trust Badge</span>
        </div>
        <div className="mt-4 flex flex-col items-center text-center">
          <div className={`flex h-14 w-14 items-center justify-center rounded-2xl ${theme.chip}`}>
            <ShieldCheck className="h-8 w-8" />
          </div>
          <div className="mt-2.5 text-[11px] font-bold uppercase tracking-wider text-bloq-muted">Trust level</div>
          <div className="font-heading text-3xl font-black leading-tight text-white">{band}</div>
          <div className="mt-1.5 flex items-center gap-2 text-sm">
            <span className={`font-black ${theme.accent}`}>BLOQ Score {score}</span>
            <span className="text-white/40">·</span>
            <span className="max-w-[140px] truncate text-white/80">{handle}</span>
          </div>
        </div>
        <div className="mt-4 rounded-xl border border-white/10 bg-black/30 px-3 py-2 text-center text-[11px] text-white/80">
          Quantum-secure, trust-gated messaging for Web3
        </div>
      </div>
      <button
        onClick={share}
        disabled={busy}
        className="mt-3 flex h-11 w-full items-center justify-center gap-2 rounded-xl bloq-gradient text-sm font-bold text-bloq-bg transition-opacity hover:opacity-90 disabled:opacity-60"
        data-testid="band-badge-share-btn"
      >
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Share2 className="h-4 w-4" />} Share my {band} badge
      </button>
    </div>
  );
}
